<template>
  <section class="l-block p-actions">
    <div class="l-block__header">
      <TitleSub icon="goal" level="h2">
        <span class="title">
          目標管理
        </span>
      </TitleSub>
      <a
        class="c-link--blank"
        href="https://support.searchwrite.jp/hc/ja/articles/8881901359631"
        target="_blank"
        >目標の設定・活用方法</a
      >
    </div>
    <div class="p-actions__content" :class="contentClass">
      <div class="p-actions-list">
        <div class="p-actions-list-item">
          <div class="p-actions-list-item__header">
            <TitleSub level="h4">
              <span class="title">最重要指標 (KGI)</span>
              <Tooltip size="sm">
                ◆KGI (Key Goal Indicator)<br />ビジネス目的達成のために、いつまでに、何を、どの水準まで目指すのかを記入してください。<br /><br />例）<br />・オーガニック経由でのコンバージョン数○件/月(○○年○月末時点)
              </Tooltip>
            </TitleSub>
            <span class="c-text--neutral">最大{{ maxLength }}文字</span>
          </div>
          <div class="p-actions-list-item__example">
            <span class="c-text--neutral"
              >例 : コンバージョン数○○件/月 (○○年○月末時点)</span
            >
          </div>
          <div class="c-alertPos">
            <Textarea
              v-model="edit.kgi"
              :rows="4"
              :disabled="isTextareaDisabled"
              :class="{
                alert: isEdited.kgi && error.kgi
              }"
            />
            <Alert v-if="isEdited.kgi && error.kgi">
              {{ error.kgi }}
            </Alert>
            <div class="p-actions-list-item__unsaved">
              <transition name="v-fade">
                <span v-if="isSomeEdited" class="c-text--unsaved c-text--accent"
                  >未保存</span
                >
              </transition>
            </div>
          </div>
        </div>
        <div class="p-actions-list-item">
          <div class="p-actions-list-item__header">
            <TitleSub level="h4">
              <span class="title">重要指標 (KPI)</span>
              <Tooltip size="sm">
                ◆KPI (Key Performance Indicator)<br />最重要指標（KGI）達成への重要指標（KPI）とするものを記入してください。<br /><br />例）<br />・オーガニック経由でのセッション数が○○/月<br />(○○年○月末時点)
              </Tooltip>
            </TitleSub>
            <span class="c-text--neutral">最大{{ maxLength }}文字</span>
          </div>
          <div class="p-actions-list-item__example">
            <span class="c-text--neutral"
              >例 : キーワード「○○」で3位以内を獲得 (○○年○月末時点)</span
            >
          </div>
          <div class="c-alertPos">
            <Textarea
              v-model="edit.kpi"
              :rows="4"
              :disabled="isTextareaDisabled"
              :class="{
                alert: isEdited.kpi && error.kpi
              }"
            />
            <Alert v-if="isEdited.kpi && error.kpi">
              {{ error.kpi }}
            </Alert>
            <div class="p-actions-list-item__unsaved">
              <transition name="v-fade">
                <span v-if="isSomeEdited" class="c-text--unsaved c-text--accent"
                  >未保存</span
                >
              </transition>
            </div>
          </div>
        </div>
        <div class="p-actions-list-item">
          <div class="p-actions-list-item__header">
            <TitleSub level="h4">
              <span class="title">行動目標</span>
              <Tooltip size="sm" position="left-top">
                ◆行動目標<br />重要指標（KPI）達成に向けた行動量を記入してください。<br /><br />例）<br />・既存改善（リライト）：○本/月<br />・既存改善（タイトル修正）：○本/月
              </Tooltip>
            </TitleSub>
            <span class="c-text--neutral">最大{{ maxLength }}文字</span>
          </div>
          <div class="p-actions-list-item__example">
            <span class="c-text--neutral"
              >例 : 新規記事作成：○本/月、毎週月曜日に改善キーワード選定</span
            >
          </div>
          <div class="c-alertPos">
            <Textarea
              v-model="edit.action"
              :rows="4"
              :disabled="isTextareaDisabled"
              :class="{
                alert: isEdited.action && error.action
              }"
            />
            <Alert v-if="isEdited.action && error.action">
              {{ error.action }}
            </Alert>
            <div class="p-actions-list-item__unsaved">
              <transition name="v-fade">
                <span v-if="isSomeEdited" class="c-text--unsaved c-text--accent"
                  >未保存</span
                >
              </transition>
            </div>
          </div>
        </div>
      </div>
      <div class="p-actions-button-area">
        <div>
          <div v-if="isError.get">
            <span class="c-text--accent">データ取得に失敗しました。</span>
            <ButtonRectangle
              size="small"
              iconLeft="arrowReturn"
              :isLoading="isLoading"
              priority="low-white"
              @click="getActions"
              >再取得</ButtonRectangle
            >
          </div>
        </div>
        <div class="c-alertPos">
          <ButtonRectangle
            @click="updateActions"
            priority="high"
            :disabled="isDisabled"
            :isLoading="isProcessing"
          >
            目標を更新する
          </ButtonRectangle>
          <Alert v-if="error.update">
            {{ error.update }}
          </Alert>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Alert from '@/components/Alert';
import ButtonRectangle from '@/components/ButtonRectangle';
import Textarea from '@/components/Textarea.vue';
import TitleSub from '@/components/TitleSub.vue';
import Tooltip from '@/components/Tooltip';
import Amplitude from '@/amplitude';
import apiProjects from '@/api/projects';
import { ERROR_MESSAGE } from '@/constants';
import Validation from '@/validation';
import axios from 'axios';

export default {
  props: ['project'],
  components: {
    Alert,
    ButtonRectangle,
    TitleSub,
    Textarea,
    Tooltip
  },
  data() {
    return {
      cancelToken: null,
      edit: {
        kgi: '',
        kpi: '',
        action: ''
      },
      params: {
        kgi: '',
        kpi: '',
        action: ''
      },
      isEdited: {
        kgi: false,
        kpi: false,
        action: false
      },
      error: {
        kgi: '',
        kpi: '',
        action: '',
        update: ''
      },
      isError: {
        get: false
      },
      isLoading: true,
      isProcessing: false,
      maxLength: 256
    };
  },
  watch: {
    'edit.kgi': {
      handler(value) {
        const text = value.trim();
        this.error.kgi = new Validation().maxLength(this.maxLength).test(text);
        this.isEdited.kgi = text !== this.params.kgi.trim() ? true : false;
      },
      immediate: true
    },
    'edit.kpi': {
      handler(value) {
        const text = value.trim();
        this.error.kpi = new Validation().maxLength(this.maxLength).test(text);
        this.isEdited.kpi = text !== this.params.kpi.trim() ? true : false;
      },
      immediate: true
    },
    'edit.action': {
      handler(value) {
        const text = value.trim();
        this.error.action = new Validation()
          .maxLength(this.maxLength)
          .test(text);
        this.isEdited.action =
          text !== this.params.action.trim() ? true : false;
      },
      immediate: true
    }
  },
  computed: {
    isDisabled() {
      return (
        this.isErrorInput ||
        !this.isSomeEdited ||
        this.isProcessing ||
        this.isError.get ||
        this.isLoading
      );
    },
    isErrorInput() {
      return this.error.kgi || this.error.kpi || this.error.action
        ? true
        : false;
    },
    isSomeEdited() {
      return this.isEdited.kgi || this.isEdited.kpi || this.isEdited.action;
    },
    isTextareaDisabled() {
      return this.isLoading || this.isProcessing || this.isError.get;
    },
    contentClass() {
      return {
        'is-editing': this.isSomeEdited
      };
    }
  },
  created() {
    this.getActions();
  },
  beforeDestroy() {
    if (this.cancelToken) {
      this.cancelToken.cancel();
    }
  },
  methods: {
    getActions() {
      this.isLoading = true;
      this.cancelToken = axios.CancelToken.source();
      apiProjects.actions
        .get(this.auth.idToken, this.project.id, this.cancelToken.token)
        .then(response => {
          this.isError.get = false;
          if (response.status === 200) {
            this.params = {
              kgi:
                response.data.kgi && response.data.kgi.content
                  ? response.data.kgi.content
                  : '',
              kpi:
                response.data.kpi && response.data.kpi.content
                  ? response.data.kpi.content
                  : '',
              action:
                response.data.action && response.data.action.content
                  ? response.data.action.content
                  : ''
            };
            this.edit = {
              ...this.params
            };
          }
        })
        .catch(() => {
          this.isError.get = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    updateActions() {
      if (this.isDisabled) {
        return;
      }
      this.isProcessing = true;
      this.error.update = '';
      const params = {
        kgi: {
          content: this.edit.kgi
        },
        kpi: {
          content: this.edit.kpi
        },
        action: {
          content: this.edit.action
        }
      };
      apiProjects.actions
        .put(params, this.auth.idToken, this.project.id)
        .then(() => {
          this.params = {
            ...this.edit
          };
          this.isEdited = {
            kgi: false,
            kpi: false,
            action: false
          };
          this.postAmplitudeLogEvent(params);
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.error.update = error.response.data.errors[0].userMessage;
          } else {
            this.error.update = ERROR_MESSAGE.other;
          }
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
    postAmplitudeLogEvent(params) {
      const properties = {
        existsKGIProjectGoal: params.kgi.content ? true : false,
        existsKPIProjectGoal: params.kpi.content ? true : false,
        existsActionProjectGoal: params.action.content ? true : false
      };
      Amplitude.postLogEvent('ClickProjectGoal', properties);
    }
  }
};
</script>
